import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";

// Wire values for the backend's AnalyticsEventType enum. Keep in sync
// with AnalyticsEvent.kt — adding a new type requires both ends.
export const ANALYTICS_EVENT = {
  integrationView: "integration_view",
  integrationInterest: "integration_interest",
  agentView: "agent_view",
  agentInterest: "agent_interest",
  resourceView: "resource_view",
  openInstance: "open_instance",
  supportClick: "support_click",
  coachingTopupClick: "coaching_topup_click",
} as const;

export type AnalyticsEventType =
  (typeof ANALYTICS_EVENT)[keyof typeof ANALYTICS_EVENT];

export type RecordEventInput = {
  type: AnalyticsEventType;
  subject?: string | undefined;
  note?: string | undefined;
};

// Fire-and-forget POST. Analytics writes never block the user — we await
// the promise so callers that want a confirmation can, but failure is
// swallowed and only logged. Use this for any dashboard click we want to
// see in admin Events.
export const recordEvent = async (input: RecordEventInput): Promise<void> => {
  try {
    await fetch("/api/events", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeader(),
      },
      body: JSON.stringify({
        type: input.type,
        subject: input.subject ?? null,
        note: input.note ?? null,
      }),
    });
  } catch {
    // intentional: analytics is best-effort
  }
};

// Shape mirrors AnalyticsController.InterestSummary.
export type InterestKind = "integration" | "agent";

export type InterestSummary = {
  kind: InterestKind;
  slug: string;
  interestedCount: number;
  userRegistered: boolean;
};

export type RegisterInterestResult =
  | { ok: true; summary: InterestSummary }
  | { ok: false; message: string };

// Detail-page helper for integrations + agents. Fires a *_view event on
// mount, hydrates the summary from the same response we already need for
// the "interest registered" state, and exposes a `register` callback for
// the explicit interest CTA.
export const useInterest = (
  kind: InterestKind,
  slug: string,
): {
  summary: InterestSummary | null;
  register: (note?: string) => Promise<RegisterInterestResult>;
} => {
  const [summary, setSummary] = useState<InterestSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Record the page-view (subject = slug). Don't await — the summary
    // GET below fires concurrently and is what hydrates the state.
    void recordEvent({
      type:
        kind === "integration"
          ? ANALYTICS_EVENT.integrationView
          : ANALYTICS_EVENT.agentView,
      subject: slug,
    });
    void fetch(
      `/api/events/interest/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`,
      { credentials: "include" },
    )
      .then(async (res) => (res.ok ? (await res.json()) as InterestSummary : null))
      .then((body) => {
        if (cancelled || !body) return;
        setSummary(body);
      })
      .catch(() => {
        // best-effort
      });
    return () => {
      cancelled = true;
    };
  }, [kind, slug]);

  const register = useCallback(
    async (note?: string): Promise<RegisterInterestResult> => {
      const type =
        kind === "integration"
          ? ANALYTICS_EVENT.integrationInterest
          : ANALYTICS_EVENT.agentInterest;
      try {
        const res = await fetch("/api/events", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...getCsrfHeader(),
          },
          body: JSON.stringify({ type, subject: slug, note: note ?? null }),
        });
        if (!res.ok) {
          return { ok: false, message: `Register failed (${res.status})` };
        }
        // Re-fetch summary so the count + currentUserHas are fresh.
        const summaryRes = await fetch(
          `/api/events/interest/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`,
          { credentials: "include" },
        );
        if (summaryRes.ok) {
          const body = (await summaryRes.json()) as InterestSummary;
          setSummary(body);
          return { ok: true, summary: body };
        }
        return { ok: false, message: `Summary fetch failed (${summaryRes.status})` };
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        return { ok: false, message };
      }
    },
    [kind, slug],
  );

  return { summary, register };
};
